<template>
  <div
    class="flex flex-col items-center mx-4 mt-3 px-2"
    id="a4-receipt"
    :class="{
      'dir-ltr': $ctx.receiptLanguage == 'en',
      'dir-rtl': $ctx.receiptLanguage == 'ar'
    }"
  >
    <div class="flex flex-col items-center w-full a4-receipt-page" style="height: 100vh;">
      <!-- header info -->
      <div id="a4-receipt-header" class="flex flex-col w-full">
        <div class="flex mt-4 justify-between items-center w-full">
          <div class="mx-8 mt-3 pb-2">
            <div class="flex items-center gap-2 mb-1">
              <img src="@/assets/icons/receipt/map-marker.svg" width="16" height="16" class="shrink-0" />
              <span>{{ settingsStore.getValue('company_address') }}</span>
            </div>
            <div class="flex items-center gap-2 mb-1">
              <img src="@/assets/icons/receipt/phone.svg" width="16" height="16" class="shrink-0" />
              <span dir="ltr">{{ settingsStore.getValue('company_phone1') + (settingsStore.getValue('company_phone2')? ` - ${settingsStore.getValue('company_phone2')}`: '') }}</span>
            </div>
            <div class="flex items-center gap-6">
              <div v-if="settingsStore.getValue('company_email')" class="flex items-center gap-2">
                <img src="@/assets/icons/receipt/email.svg" width="16" height="16" class="shrink-0" />
                <span>{{ settingsStore.getValue('company_email') }}</span>
              </div>
              <div v-if="settingsStore.getValue('company_website')" class="flex items-center gap-2">
                <img src="@/assets/icons/receipt/web.svg" width="16" height="16" class="shrink-0" />
                <span>{{ settingsStore.getValue('company_website') }}</span>
              </div>
            </div>
          </div>

          <div class="mx-8">
            <img :src="settingsStore.getValue('receipt_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('receipt_logo'): '/images/logo.svg'" :width="settingsStore.getValue('receipt_logo_width')" :style="{'margin-top': settingsStore.getValue('receipt_logo_top_margin') + 'px', 'margin-right': settingsStore.getValue('receipt_logo_right_margin') + 'px'}" alt="" />
          </div>
        </div>

        <div class="flex flex-col w-full items-center">
          <div class="w-11/12 h-[3px] rounded-full"
              :style="{background: `linear-gradient(to right, transparent, ${settingsStore.getValue('receipt_branding_color') || '#585954'}, transparent)`}"></div>
        </div>
      </div>

      <div id="a4-receipt-body" class="w-full">
        <!-- title -->
        <div class="flex flex-col w-full mt-2 items-center">
          <h2 class="bg-gray-200 rounded py-1 px-16 font-bold mt-3 capitalize">
            {{ receiptTranslate("Return Receipt", "receipt.returnReceipt") }}
          </h2>
        </div>

        <!-- order type info and barcode -->
        <div class="flex w-full justify-around">
          <div>
            <table class="w-full text-sm">
              <tr>
                <td>
                  <p>{{ receiptTranslate("Type", "receipt.type") }}</p>
                </td>
                <td>
                  <p class="font-bold mx-5">
                    <span v-if="order.invoice.paymentType == 'direct'">{{
                      receiptTranslate("Direct", "receipt.direct")
                    }}</span>
                    <span v-if="order.invoice.paymentType == 'credit_sale'">{{
                      receiptTranslate("On Account", "receipt.credit")
                    }}</span>
                    <span v-if="order.invoice.paymentType == 'layaway'">{{
                      receiptTranslate("Layaway", "receipt.layaway")
                    }}</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p>
                    {{ receiptTranslate("Customer", "receipt.customer") }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5">
                    {{
                      order.invoice.customer?.contactDisplayName
                        ? `${order.invoice.customer?.contactDisplayName} ${
                            order.invoice.customer?.businessName
                              ? " - " + order.invoice.customer?.businessName
                              : ""
                          }`
                        : receiptTranslate(
                            "Cash Customer",
                            "receipt.cashCustomer"
                          )
                    }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p>
                    {{ receiptTranslate("Location", "receipt.location") }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5">
                    {{ order.location?.enName || "" }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p>
                    {{ receiptTranslate("Order No", "receipt.orderNo") }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5">{{ order.externalID }}</p>
                </td>
              </tr>
              <tr v-if="settingsStore.getValue('hide_cashier_name_in_receipt') != 'true'">
                <td>
                  <p>
                    {{ receiptTranslate("Cashier", "receipt.cashier") }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5">
                    {{ orderChange?.user?.fullName }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p>
                    {{ receiptTranslate("Date & Time", "receipt.dateAndTime") }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5" dir="ltr">
                    {{ parseFormat(orderChange?.createdAt) }}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <p>
                    {{ receiptTranslate("Notes", "receipt.notes") }}
                  </p>
                </td>
                <td>
                  <p class="font-bold mx-5" dir="ltr">
                    {{ orderChange?.notes }}
                  </p>
                </td>
              </tr>
            </table>
          </div>

          <div class="flex flex-col items-center">
            <div>
              <svg id="barcode" class="mt-3"></svg>
              <div class="flex items-center justify-center">
                <span class="font-bold mx-5" style="font-size: calc(20px * var(--receipt-font-scale, 1));">{{
                  barcodeText
                }}</span>
              </div>
            </div>
            <div
              v-if="order?.invoice.pouchID"
              class="flex flex-col w-full mt-2 items-center"
            >
              <h2 class="bg-gray-200 rounded py-1 px-2 flex">
                <span>{{ receiptTranslate("Pouch #", "receipt.pouch") }}</span>
                <span class="flex justify-end custom-bold mx-2">
                  {{ order?.invoice.pouchID }}
                </span>
              </h2>
            </div>
          </div>
        </div>

        <!-- items -->
        <div class="w-full px-5 my-2 text-sm">
          <table class="receipt-table w-full">
            <thead>
              <tr class="text-sm">
                <th>
                  {{ receiptTranslate("Item Name", "receipt.itemName") }}
                </th>
                <th>{{ receiptTranslate("Qty", "receipt.qty") }}</th>
                <th>{{ receiptTranslate("Price", "receipt.price") }}</th>
                <th>
                  {{ receiptTranslate("Final Price", "receipt.finalPrice") }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(oi, i) in getReturnedOrderItems()"
                :key="i"
                class="text-sm"
                :class="{ 'receipt-row-border': !oi.item.isSerialized }"
              >
                <td class="text-sm reciept-cell">
                  <div>
                    <span>{{ oi.item.receiptName ? oi.item.receiptName: oi.item.name }}</span>
                    <span v-if="oi.packInfo?.packID" class="mx-1">{{ ` - ${getPackName(oi)}, ${oi.packInfo.packQuantity} ${t('pcs')}` }}</span>
                  </div>
                  <div v-if="oi.extraProperties?.batchNumber">
                    <span class="my-1 bg-gray-200 rounded-md px-1">{{ oi.extraProperties?.batchNumber }} - {{ oi.extraProperties?.expiryDate ? formatDate(oi.extraProperties?.expiryDate, 'DD-MM-YYYY') : '' }}</span>
                  </div>
                  <small>{{ oi.notes }}</small>
                </td>

                <td>{{ oi.quantity }}</td>
                <td>
                  {{ formatDinero(oi.itemSalePriceDinero) }}
                </td>
                <td>{{ formatDinero(oi.soldAtPriceDinero!.multiply(oi.quantity)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- payment info -->
        <div class="text-sm flex flex-col w-full items-end">
          <div class="flex-col w-5/12 mr-10">
            <div class="px-4 w-full mt-2" v-if="transactions.length">
              <div style="color: #6e6e6e; padding-left: 2px">
                <h4 class="text-lg">
                  {{
                    receiptTranslate("Transactions Info", "receipt.transactionsInfo")
                  }}
                </h4>
              </div>

              <div
                v-for="customerPayment in transactions"
                :key="customerPayment.id"
              >
                <table
                  class="payment-table"
                  :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }"
                >
                  <tr>
                    <td>
                      <p class="p-0">
                        {{
                          receiptTranslate("Payment ID", "receipt.paymentID")
                        }}: {{ customerPayment.externalID }}
                      </p>
                    </td>
                    <td>
                      <p class="text-right">
                        {{ parseFormat(customerPayment.createdAt) }}
                      </p>
                    </td>
                  </tr>
                </table>

                <table
                  class="payment-table"
                  :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }"
                >
                  <tr
                    v-if="
                      customerPayment.receiptType != 'return' && customerPayment.receiptType != 'refund' &&
                        customerPayment.paymentMethod === 'cash'
                    "
                  >
                    <td>{{ receiptTranslate("Cash", "receipt.cash") }}</td>
                    <td>
                      <p v-if="customerPayment.paidBeforeChangeAmount && customerPayment.paidBeforeChangeAmount > 0" class="text-right">
                        {{
                          formatDinero(customerPayment.paidBeforeChangeDinero)
                        }}
                      </p>
                      <p v-else class="text-right">
                        {{ formatDinero(customerPayment.amountDinero) }}
                      </p>
                    </td>
                  </tr>

                  <tr
                    v-if="
                      customerPayment.receiptType != 'return' && customerPayment.receiptType != 'refund' &&
                        customerPayment.paymentMethod === 'credit'
                    "
                  >
                    <td>{{ receiptTranslate("Credit", "receipt.customerCredit") }}</td>
                    <td>
                      <p v-if="customerPayment.paidBeforeChangeAmount && customerPayment.paidBeforeChangeAmount > 0" class="text-right">
                        {{
                          formatDinero(customerPayment.paidBeforeChangeDinero)
                        }}
                      </p>
                      <p v-else class="text-right">
                        {{ formatDinero(customerPayment.amountDinero) }}
                      </p>
                    </td>
                  </tr>

                  <tr
                    v-if="
                      customerPayment.receiptType == 'refund' &&
                        (customerPayment.paymentMethod === 'cash' || customerPayment.paymentMethod === 'credit')
                    "
                  >
                    <td v-if="customerPayment.paymentMethod === 'cash'">{{ receiptTranslate("Cash", "receipt.cash") }}</td>
                    <td v-else>{{ receiptTranslate("Credit", "receipt.customerCredit") }}</td>
                    <td>
                      <p class="text-right">
                        ({{ formatDinero(customerPayment.amountDinero)}})
                      </p>
                    </td>
                  </tr>

                  <tr
                    v-if="
                      customerPayment.receiptType == 'return' &&
                        customerPayment.paymentMethod === 'cash'
                    "
                  >
                    <td>{{ receiptTranslate("Cash", "receipt.cash") }}</td>
                    <td>
                      <p class="text-right">
                        ({{ formatDinero(customerPayment.amountDinero)}})
                      </p>
                    </td>
                  </tr>

                  <tr
                    v-if="
                      customerPayment.receiptType == 'return' &&
                        customerPayment.paymentMethod === 'credit'
                    "
                  >
                    <td>{{ receiptTranslate("Credit", "receipt.customerCredit") }}</td>
                    <td>
                      <p class="text-right">
                        ({{ formatDinero(customerPayment.amountDinero)}})
                      </p>
                    </td>
                  </tr>

                  <tr
                    v-if="
                      customerPayment.receiptType != 'return' &&
                        customerPayment.paymentMethod == 'debit-card'
                    "
                  >
                    <td>E-Payment</td>
                    <td>
                      <p class="text-right">
                        {{
                            formatDinero(customerPayment.amountDinero)
                        }}
                      </p>
                    </td>
                  </tr>

                  <tr
                    v-if="
                      customerPayment.receiptType != 'return' && customerPayment.receiptType != 'refund' &&
                        customerPayment.paymentMethod === 'cash' && customerPayment.changeDueAmount && customerPayment.changeDueAmount > 0
                    "
                  >
                    <td>
                      {{ receiptTranslate("Change Due", "receipt.changeDue") }}
                    </td>
                    <td>
                      <p class="text-right">
                        {{ formatDinero(customerPayment.changeDueDinero) }}
                      </p>
                    </td>
                  </tr>

                  <tr
                    v-if="customerPayment.multiCurrencyBreakdown?.length === 2 && customerPayment.multiCurrencyBreakdown[1].amount > 0"
                  >
                    <td>
                      {{ receiptTranslate("Payment Currencies", "receipt.paymentCurrencies") }}
                    </td>
                    <td>
                      <p
                        class="text-right"
                      >
                        {{formatMoney(customerPayment.multiCurrencyBreakdown[0].amount, customerPayment.multiCurrencyBreakdown[0].currency)}} + {{formatMoney(customerPayment.multiCurrencyBreakdown[1].amount, customerPayment.multiCurrencyBreakdown[1].currency)}}
                      </p>
                    </td>
                  </tr>

                  <tr v-if="customerPayment.receiptType != 'return'">
                    <td>
                      {{ receiptTranslate("Balance", "receipt.balance") }}
                    </td>
                    <td>
                      <p class="text-right">
                        {{ formatDinero(customerPayment.balanceDinero) }}
                      </p>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="a4-receipt-footer"
        v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
        class="flex justify-center self-center w-full mt-auto"
      >
        <div
          class="flex items-center justify-between py-4 px-4 mt-2"
          style="bottom: 0px; width: 95%; border-top: 2px solid #BDC7D6"
        >
          <p><img src="@/assets/images/thecky-qr.png" style="height: 25px;"/></p>
          <p><img src="@/assets/images/footer.svg" style="height: 25px;"/></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

import {
  i18nOrderMessages,
  Order,
  OrderChange,
  OrderItem
} from "@/models/sales/Order";
import { formatDate, parseFormat } from "@/utils/date";
import { useI18n } from "vue-i18n";
import { formatDinero, formatMoney } from "@/utils/money";
import { Transaction } from "@/models/accounting/Transaction";
import JsBarcode from "jsbarcode";
import {useSettingsStore} from "@/stores/settingsStore";
import { receiptPrintCSS } from "@/utils/receiptPrint";

export default defineComponent({
  components: {},

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...i18nOrderMessages.en
        },
        ar: {
          ...i18nOrderMessages.ar,
          receipt: {
            returnReceipt: "إيصال الاسترجاع",
            type: "نوع الطلب",
            direct: "مباشر",
            credit: "آجل",
            layaway: "عربون",
            customer: "الزبون",
            cashCustomer: "زبون",
            location: "المتجر",
            orderNo: "رقم الطلب",
            cashier: "موظف الكاشير",
            dateAndTime: "التاريخ والوقت",
            notes: "الملاحظات",
            orderNotes: "ملاحظات الطلب",
            itemName: "اسم المنتج",
            qty: "الكمية",
            price: "السعر",
            finalPrice: "السعر النهائي",
            totalBeforeDiscounts: "المبلغ الكلي قبل التخفيض",
            totalDiscount: "المبلغ الكلي للتخفيض",
            totalDue: "الاجمالي",
            totalItemsSold: "عدد المنتجات المباعة",
            transactionsInfo: "الحركات المالية",
            paymentID: "رقم الحركة",
            paymentMethod: "طريقة الدفع",
            cash: "كاش",
            changeDue: "المتبقي",
            totalPaid: "المبلغ المدفوع الكلي",
            balance: "المتبقي",
            paymentNotes: "ملاحظات الحركة",
            customerCredit: "رصيد الزبون",
            invoiceTypes: {
              full: 'كامل',
              credit: 'آجل',
              layaway: 'عربون',
              cashOnDelivery: 'دفع عند التوصيل',
            },
          }
        }
      }
    });

    const settingsStore = useSettingsStore();

    return { t, settingsStore, formatDate };
  },
  data() {
    return {
      receiptID: ""
    };
  },
  props: {
    order: {
      type: Order,
      required: true
    },
    orderChange: {
      type: OrderChange
    },
    transactions: {
      type: Array as PropType<Array<Transaction>>,
      required: true
    },
    barcodeText: {
      type: String,
      required: true
    }
  },
  async mounted() {
    if (this.orderChange) {
      if (this.transactions.length) {
        this.receiptID = this.transactions[0].externalID!;
      }

      setTimeout(() => {
        JsBarcode("#barcode", String(this.orderChange!.externalID!.slice()), {
          height: 50,
          displayValue: false
        });
      }, 100);
    }
  },
  created() {
    // Add print CSS when component is created
    this.addPrintCSS();
  },
  beforeUnmount() {
    // Remove print CSS when component is about to be destroyed
    this.removePrintCSS();
  },
  methods: {
    parseFormat,
    formatDinero,
    formatMoney,
    print() {
      window.print();
    },
    calculateTotalItemsSold() {
      let total = 0;
      for (const item of this.order.orderItems) {
        total += item.quantity;
        total -= item.returnedQuantity;
      }

      return total;
    },
    // Function to find returned order items
    getReturnedOrderItems(): Array<OrderItem> {
      if (!this.orderChange) {
        return [];
      }
      const returnedOrderItems: Array<OrderItem> = [];
        console.log('salom')
        console.log(this.orderChange)

      Object.keys(this.orderChange.orderChangeItems).forEach(itemId => {
        const item = this.order.orderItems.find(
          orderItem => orderItem.id === itemId
        ) as OrderItem;

        item.quantity = this.orderChange!.orderChangeItems[itemId].quantity;

        if (item && item.returnedQuantity > 0) {
          returnedOrderItems.push(item);
        }
      });

      return returnedOrderItems;
    },
    addPrintCSS() {
      // Create a style element with a unique id
      const style = document.createElement("style");
      style.setAttribute("id", "print-css");
      style.textContent = receiptPrintCSS(this.$ctx.getReceiptSize());
      // Append the style to the document head
      document.head.appendChild(style);
    },
    removePrintCSS() {
      // Find and remove the style element
      const style = document.querySelector("#print-css");
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    receiptTranslate(value: string, translateKey: string): string {
      return this.$ctx.receiptLanguage == "en"
        ? value
        : this.t(translateKey, this.$ctx.receiptLanguage);
    },
    getPackName(oi: OrderItem) {
      for (const ip of oi.item.itemPacks) {
        if (ip.id == oi.packInfo.itemPackID) {
          return this.$ctx.locale == 'en' ? ip.pack?.enName: ip.pack?.arName
        }
      }
    }
  }
});
</script>
<style>
.thecky-site {
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 3px;
}

#a4-receipt {
  width: 210mm;
  height: 297mm;
  overflow: scroll;
  background-color: #fff;
}

@media print {
  body * {
    page-break-after: avoid;
    visibility: hidden;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #a4-receipt,
  #a4-receipt * {
    visibility: visible;
  }

  #a4-receipt {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    margin: 0cm;
  }

  #a4-receipt-header {
    top: 0;
  }

  #a4-receipt-body {
    position: relative;
  }

  p,
  div {
    page-break-inside: avoid;
  }
}
</style>
